import axios from "axios";
import { detectUserLocale, invalidateLocaleCache } from "./utils/multi-language";
import { baseURL } from "./utils/constants";
import { getValidUserId } from "./utils/uuid";
import i18n from "./i18n";
import { captureFrontendException } from "./sentry/init";

const authAxios = axios.create({
  baseURL,
  // Cap requests so a hung/slow backend surfaces a timeout error instead of an
  // infinite "loading" spinner (the booking "Processing…" hang). The booking
  // flow now returns promptly (BE side-effects moved off the critical path),
  // so 60s sits well above normal latency while still bounding worst-case hangs.
  timeout: 60000,
});

authAxios.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem("JWT_TOKEN");
    const userId = getValidUserId();

    const { community, currency } = await detectUserLocale();

    // Use i18n.language as the single source of truth for language,
    // ensuring API content language always matches UI label language.
    config.headers["Language"] = i18n.language || "en";
    config.headers["Community"] = community;
    config.headers["Currency"] = currency;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (userId && !config.url.includes("user_id")) {
      const url = new URL(config.url, config.baseURL || window.location.origin);
      url.searchParams.set("user_id", userId);
      config.url = url.pathname + url.search;
    }

    // Add ngrok-skip-browser-warning header to bypass ngrok warning page
    if (baseURL && baseURL.includes("ngrok")) {
      config.headers["ngrok-skip-browser-warning"] = "true";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

authAxios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      // Non-critical background calls (e.g. the checkout credit-balance refresh)
      // opt out of the destructive logout via `skipAuthRedirect` — a transient
      // 401 there must never nuke an active session mid-checkout.
      if (error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }

      localStorage.removeItem("JWT_TOKEN");
      localStorage.removeItem("USER_ID");
      localStorage.removeItem("locale");
      localStorage.removeItem("community");
      localStorage.removeItem("currency");
      // Drop the in-memory locale memo so a subsequent login recomputes fresh
      // (community/currency/language) rather than serving the logged-out result.
      invalidateLocaleCache();

      if (window.isInApp) {
        window.postMessage("logout");
      } else {
        const originalPath = window.location.pathname + window.location.search;

        if (!originalPath.includes("reroute")) {
          window.location = `/login?reroute=${originalPath}`;
        }
      }
    } else {
      // Surface TRANSIENT backend/network failures (the class that drives the
      // intermittent relogin / saved-cards / 502 reports) to Sentry. Skip 4xx
      // (expected validation/not-found) to keep the signal clean.
      const isTransient =
        !error.response ||
        error.code === "ECONNABORTED" ||
        [500, 502, 503, 504].includes(status);
      if (isTransient) {
        captureFrontendException(error, {
          tags: {
            area: "authAxios",
            url: error.config?.url?.split("?")[0],
            status: status ?? "network",
          },
          level: "warning",
        });
      }
    }
    return Promise.reject(error);
  }
);

export const authAxiosFetcher = (url) => authAxios.get(url).then((res) => res.data);

export default authAxios;
